import { getIntentDetails, parseIntent } from "./intent";

export const AUTH_STATUS_REASON_COPY = Object.freeze({
    blocked_sign_in: {
        title: "Sign-in not available",
        body: "We couldn't continue this sign-in attempt for the requested workspace.",
        nextStep: "Return to sign-in and check the details you entered, or contact support if you believe access should be available.",
    },
    account_not_found: {
        title: "Access not available",
        body: "We verified your sign-in, but we could not match it to an active workspace account.",
        nextStep: "Confirm you are using the correct sign-in details and contact your administrator or support team if access should be available.",
    },
    not_yet_available: {
        title: "Workspace not available yet",
        body: "This destination is recognised, but it is not currently available through shared sign-in.",
        nextStep: "Choose another available workspace or return later when this destination is live.",
    },
    invalid_intent: {
        title: "Destination not recognised",
        body: "This sign-in link did not include a supported destination.",
        nextStep: "Return to the secure entry page and choose the correct workspace.",
    },
    access_unconfirmed: {
        title: "We couldn't confirm your workspace",
        body: "Your sign-in was successful, but we could not safely determine which workspace to open.",
        nextStep: "Try again shortly. If this keeps happening, contact support and quote the support reference below.",
    },
    access_denied: {
        title: "Access not available",
        body: "Your sign-in was successful, but this account is not currently authorised for the requested workspace.",
        nextStep: "Contact your administrator if you believe access should be available.",
    },
    bootstrap_issue_failed: {
        title: "We couldn't open your workspace",
        body: "Your sign-in was confirmed, but the secure workspace handoff could not be completed.",
        nextStep: "Try again. If the problem continues, contact support and include the support reference.",
    },
    session_invalid: {
        title: "We couldn't validate your session",
        body: "Your session is no longer trusted for this workspace.",
        nextStep: "Sign in again to continue.",
    },
    session_expired: {
        title: "Your session expired",
        body: "For your security, this session has ended.",
        nextStep: "Sign in again to continue your work.",
    },
    session_revoked: {
        title: "You have been signed out",
        body: "This workspace session is no longer active.",
        nextStep: "Sign in again if you still need access.",
    },
    access_changed: {
        title: "Your access changed",
        body: "Your sign-in was successful, but your current workspace access could no longer be confirmed.",
        nextStep: "Sign in again or contact your administrator if this should still be available.",
    },
    recovery_required: {
        title: "Manual recovery required",
        body: "We could not safely continue with automated verification for this account.",
        nextStep: "Submit a recovery request so a staff member can review your account and help restore access.",
    },
});

export function buildSupportReference({ reason, intent, requestId }) {
    const safeIntent = parseIntent(intent, "application").toUpperCase();
    const safeReason = String(reason || "access_unconfirmed")
        .trim()
        .replace(/[^a-z0-9]+/gi, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();

    if (requestId) {
        return `${safeIntent}-${safeReason}-${String(requestId).slice(0, 8).toUpperCase()}`;
    }

    return `${safeIntent}-${safeReason}`;
}

export function getAuthStatusCopy(intent, reason, supportMessage = null) {
    const details = getIntentDetails(intent);
    const base = AUTH_STATUS_REASON_COPY[reason] || AUTH_STATUS_REASON_COPY.access_denied;

    return {
        title: reason === "not_yet_available" ? base.title : `${base.title}${details.shortLabel ? `: ${details.shortLabel}` : ""}`,
        body: supportMessage || base.body,
        nextStep: base.nextStep,
    };
}
